import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Play, 
  Pause, 
  Volume2, 
  Save,
  AlertCircle,
  Users,
  Loader2,
  Brain,
  Home,
  ArrowUpRight
} from 'lucide-react';
import { SpeakerLabels, SpeakerSegment, AudioFile } from '../types';
import { startSpeakerInferenceSession } from '../services/agentService';
import { SpeakerAudioService } from '../services/speakerAudioService';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { getSpeakerColorValue } from '../utils/speakerColors';

interface SpeakerLabelingProps {
  sessionId?: string;
  speakers: number[];
  speakerSegments: Map<number, SpeakerSegment[]>;
  audioFile: AudioFile | null;
  onLabelsComplete: (labels: SpeakerLabels) => void;
  initialLabels?: SpeakerLabels;
  className?: string;
  // YouTube mode props
  isYouTubeMode?: boolean;
  transcriptionChecksum?: string;
}

const SpeakerLabeling: React.FC<SpeakerLabelingProps> = ({
  sessionId,
  speakers,
  speakerSegments,
  audioFile,
  onLabelsComplete,
  initialLabels,
  className = '',
  isYouTubeMode = false,
  transcriptionChecksum
}) => {
  const { token } = useAuth();
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [labels, setLabels] = useState<SpeakerLabels>(() => {
    const initial: SpeakerLabels = {};
    speakers.forEach(id => {
      initial[id] = initialLabels?.[id] || `Speaker ${id}`;
    });
    return initial;
  });
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [isInferring, setIsInferring] = useState(false);
  const [hasRunInference, setHasRunInference] = useState(false);
  const [inferenceAgentSessionId, setInferenceAgentSessionId] = useState<string | null>(null);
  const audioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  // Clear audio cache when transcriptionChecksum changes (e.g., after audio recovery)
  useEffect(() => {
    // Clean up old audio references when session changes
    audioRefs.current.forEach((audio) => {
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }
      audio.pause();
    });
    audioRefs.current.clear();
    setPlayingAudio(null);
    setError('');
    setHasRunInference(false); // Reset inference flag for new session
    setInferenceAgentSessionId(null);
  }, [transcriptionChecksum]);

  // Function to infer speaker names using AI
  const inferSpeakerNames = async () => {
    if (!sessionId || speakers.length === 0 || isInferring || hasRunInference) {
      return; // Skip inference for no speakers, missing episode scope, already inferring, or already completed
    }

    try {
      setIsInferring(true);
      setError('');

      const result = await startSpeakerInferenceSession({
        sessionId,
        enableWebSearch: true,
      }, token || undefined);
      setInferenceAgentSessionId(result.agentSessionId);
      setHasRunInference(true);

    } catch (error) {
      console.error('Speaker name inference failed:', error);
      setError('Failed to start the speaker inference agent. You can still label speakers manually.');
    } finally {
      setIsInferring(false);
    }
  };

  // Check if labels are still defaults (not manually set)
  const hasDefaultLabels = () => {
    if (!initialLabels) return true;
    return speakers.every(id => 
      !initialLabels[id] || initialLabels[id] === `Speaker ${id}`
    );
  };

  // Trigger speaker inference on component mount
  useEffect(() => {
    if (transcriptionChecksum && speakers.length > 0 && hasDefaultLabels()) {
      // Only run inference if we don't have custom labels and there are speakers
      console.log('[SPEAKER-INFERENCE] Triggering automatic inference on mount');
      inferSpeakerNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptionChecksum, speakers.length]);

  const handleLabelChange = (speakerId: number, label: string) => {
    setLabels(prev => ({
      ...prev,
      [speakerId]: label
    }));
  };

  const playAudioSample = async (speakerId: number) => {
    try {
      setError('');
      
      // Stop any currently playing audio
      if (playingAudio !== null) {
        const currentAudio = audioRefs.current.get(playingAudio);
        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }
      }

      if (!transcriptionChecksum) {
        setError(`Audio not available for Speaker ${speakerId}`);
        return;
      }

      let audio = audioRefs.current.get(speakerId);
      if (!audio) {
        try {
          const audioUrl = await SpeakerAudioService.fetchSpeakerSampleUrl(
            transcriptionChecksum,
            speakerId,
            token || undefined
          );
          audio = new Audio(audioUrl);
          audioRefs.current.set(speakerId, audio);
        } catch (fetchError) {
          console.error('[SPEAKER-LABELING] Failed to fetch speaker sample:', fetchError);
          setError(`Failed to load audio sample for Speaker ${speakerId}. Audio may not be available for this session.`);
          return;
        }

        audio.addEventListener('ended', () => {
          setPlayingAudio(null);
        });
        
        audio.addEventListener('error', () => {
          setError(`Failed to play audio sample for Speaker ${speakerId}`);
          setPlayingAudio(null);
        });
      }

      audio.currentTime = 0;
      setPlayingAudio(speakerId);
      await audio.play();
      
    } catch (error) {
      console.error('Audio playback error:', error);
      setError(`Failed to play audio sample for Speaker ${speakerId}`);
      setPlayingAudio(null);
    }
  };

  const stopAudioSample = (speakerId: number) => {
    const audio = audioRefs.current.get(speakerId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingAudio(null);
  };

  const handleSaveLabels = () => {
    // Fill in default labels for speakers without custom names
    const completedLabels: SpeakerLabels = {};
    speakers.forEach(speakerId => {
      completedLabels[speakerId] = labels[speakerId] || `Speaker ${speakerId}`;
    });
    
    onLabelsComplete(completedLabels);
  };

  const getSpeakerSegmentCount = (speakerId: number): number => {
    return speakerSegments.get(speakerId)?.length || 0;
  };

  const getSpeakerDuration = (speakerId: number): number => {
    const segments = speakerSegments.get(speakerId) || [];
    return segments.reduce((total, segment) => total + (segment.end - segment.start), 0);
  };

  // Generate timeline data for speaker visualization
  const generateTimelineData = () => {
    const allSegments: Array<{ start: number; end: number; speaker: number }> = [];
    
    // Collect all segments
    speakers.forEach(speakerId => {
      const segments = speakerSegments.get(speakerId) || [];
      segments.forEach(segment => {
        allSegments.push({
          start: segment.start,
          end: segment.end,
          speaker: speakerId
        });
      });
    });
    
    // Sort by start time
    allSegments.sort((a, b) => a.start - b.start);
    
    // Calculate total duration
    const totalDuration = allSegments.length > 0 
      ? Math.max(...allSegments.map(s => s.end))
      : 0;
    
    return { segments: allSegments, totalDuration };
  };

  if (speakers.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`card ${className}`}
      >
        <div className="text-center py-8">
          <AlertCircle size={48} className="text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            No Speakers Detected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            The audio file doesn't contain multiple speakers or speaker diarization failed.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card ${className}`}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg">
              <Users size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200">Label Speakers</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {audioFile && !isYouTubeMode
                  ? "Identify the speakers by listening to audio samples"
                  : isYouTubeMode 
                  ? "Identify the speakers by reviewing their segments (audio samples temporarily unavailable for YouTube videos)"
                  : "Identify the speakers by reviewing their segments (audio samples not available)"
                }
              </p>
            </div>
          </div>
          
          {/* Navigation and AI Inference Status and Controls */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Return to Home"
            >
              <Home size={18} />
            </button>
            
            {transcriptionChecksum && (
              <div className="flex items-center space-x-2">
              {isInferring && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400"
                >
                  <Loader2 size={16} className="animate-spin" />
                  <span>AI is analyzing...</span>
                </motion.div>
              )}
              
              {!isInferring && (
                <button
                  onClick={inferSpeakerNames}
                  className="flex items-center px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                  title={speakers.length > 1 ? "Start the speaker inference agent" : "Start the speaker naming agent"}
                >
                  <Brain size={16} />
                </button>
              )}
            </div>
            )}
          </div>
        </div>

        {inferenceAgentSessionId && !isInferring && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Brain size={16} className="text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Speaker inference agent started
                </span>
              </div>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-agent', {
                  detail: { agentSessionId: inferenceAgentSessionId },
                }))}
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Open agent
                <ArrowUpRight size={12} />
              </button>
            </div>
            <p className="mt-2 text-xs text-blue-700 dark:text-blue-200">
              The agent will infer speaker labels through the ReScript CLI and save them back onto this session. Open the agent tab if you want to supervise or interject.
            </p>
          </motion.div>
        )}

        {/* Speaker Timeline Visualization */}
        {speakers.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4"
          >
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Speaker Timeline</h4>
            {(() => {
              const { segments, totalDuration } = generateTimelineData();
              return (
                <div className="space-y-2">
                  {/* Timeline bar */}
                  <div className="relative h-8 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                    {segments.map((segment, index) => {
                      const startPercent = (segment.start / totalDuration) * 100;
                      const widthPercent = ((segment.end - segment.start) / totalDuration) * 100;
                      
                      return (
                        <div
                          key={index}
                            className="absolute h-full opacity-80"
                            style={{
                            backgroundColor: getSpeakerColorValue(segment.speaker, isDarkMode),
                            left: `${startPercent}%`,
                            width: `${widthPercent}%`
                          }}
                          title={`Speaker ${segment.speaker}: ${Math.round(segment.start)}s - ${Math.round(segment.end)}s`}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 text-xs">
                    {speakers.map(speakerId => {
                      const duration = getSpeakerDuration(speakerId);
                      const percentage = totalDuration > 0 ? ((duration / totalDuration) * 100).toFixed(1) : '0';
                      
                      return (
                        <div key={speakerId} className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: getSpeakerColorValue(speakerId, isDarkMode) }}
                          />
                          <span className="text-gray-600 dark:text-gray-400">
                            {labels[speakerId] || `Speaker ${speakerId}`} ({percentage}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Time markers */}
                  <div className="relative text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex justify-between">
                      <span>0:00</span>
                      <span>{Math.floor(totalDuration / 60)}:{(totalDuration % 60).toFixed(0).padStart(2, '0')}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

        <div className="space-y-4">
          {speakers.map((speakerId) => (
            <motion.div
              key={speakerId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: speakerId * 0.1 }}
              className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
            >
              <div className="flex items-center space-x-3 flex-1">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: getSpeakerColorValue(speakerId, isDarkMode) }}
                  />
                  <div className="p-2 bg-white dark:bg-gray-700 rounded-lg shadow-sm">
                    <User size={16} className="text-gray-600 dark:text-gray-300" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Speaker {speakerId}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {getSpeakerSegmentCount(speakerId)} segments • {Math.round(getSpeakerDuration(speakerId))}s
                    </span>
                  </div>
                  
                  <input
                    type="text"
                    value={labels[speakerId] || ''}
                    onChange={(e) => handleLabelChange(speakerId, e.target.value)}
                    placeholder={`Enter name for Speaker ${speakerId}...`}
                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-200 transition-all duration-200"
                  />
                </div>
              </div>

              {audioFile && !isYouTubeMode && (
                <button
                  onClick={() => 
                    playingAudio === speakerId 
                      ? stopAudioSample(speakerId)
                      : playAudioSample(speakerId)
                  }
                  disabled={playingAudio !== null && playingAudio !== speakerId}
                  className={`
                    flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-200
                    ${playingAudio === speakerId
                      ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700'
                      : 'bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-700'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  {playingAudio === speakerId ? (
                    <>
                      <Pause size={16} />
                      <span className="text-sm">Stop</span>
                    </>
                  ) : (
                    <>
                      <Play size={16} />
                      <span className="text-sm">Play Sample</span>
                    </>
                  )}
                </button>
              )}
            </motion.div>
          ))}
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-center space-x-2"
            >
              <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <Volume2 size={16} />
            <span>Play samples to identify speakers</span>
          </div>
          
          <button
            onClick={handleSaveLabels}
            className="btn-primary flex items-center space-x-2"
          >
            <Save size={16} />
            <span>Save Labels & Generate Transcript</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SpeakerLabeling; 
